import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyGetVisitasRoamQuery } from '../../../services/visitasApi';
import { removeEmptyFields } from '../../../utils/common';
import { VISIT_TABS_INDEX } from '../../../redux/slices/visitsSlice';
import { VisitaEstado } from '../../Visitas/consts/visitasStatus';

export interface VisitasRoamFiltros {
    idOrganismoCentral?: number;
    idOrganismo?: number;
    idEstablecimiento?: number;
    idProvinciaAccidente?: number;
    idLocalidadAccidente?: number;
    estado?: number;
    empleado?: string;
    nroRoam?: string;
    lugarOcurrencia?: string;
    fechaVisita?: string;
    idTipoVisita?: number;
    vencidas?: boolean;
    proximoVencer?: boolean;
    skip?: number;
    take?: number;
}

const FILTROS_INICIALES: VisitasRoamFiltros = {
    skip: 0,
    take: 10
};

const DEBOUNCE_MS = 400;

const TAB_ESTADOS: Partial<Record<VISIT_TABS_INDEX, number | number[]>> = {
    [VISIT_TABS_INDEX.REJECTED]: [VisitaEstado.RECHAZADO, VisitaEstado.SIN_ASIGNAR],
    [VISIT_TABS_INDEX.PENDING]: VisitaEstado.PENDIENTE,
    [VISIT_TABS_INDEX.PROGRAMMED]: VisitaEstado.PROGRAMADA,
    [VISIT_TABS_INDEX.IN_PROGRESS]: VisitaEstado.EN_PROCESO,
    [VISIT_TABS_INDEX.IN_REVISION]: VisitaEstado.REALIZADA,
    [VISIT_TABS_INDEX.CLOSED]: [
        VisitaEstado.FALLIDA,
        VisitaEstado.PRESENTADO,
        VisitaEstado.RECHAZADO,
        VisitaEstado.CANCELADA
    ]
};

function useVisitasRoam(
    initialTabIndex: VISIT_TABS_INDEX = VISIT_TABS_INDEX.PENDING,
    initialEstado?: number
) {
    const initialFiltros: VisitasRoamFiltros =
        initialEstado !== undefined
            ? { ...FILTROS_INICIALES, estado: initialEstado }
            : FILTROS_INICIALES;

    const [filtros, setFiltros] = useState<VisitasRoamFiltros>(initialFiltros);
    const [currentTabIndex, setCurrentTabIndex] = useState<VISIT_TABS_INDEX>(initialTabIndex);
    const [getVisitasRoam, result] = useLazyGetVisitasRoamQuery();
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchVisitas = useCallback(
        (params: VisitasRoamFiltros, tabIndex: VISIT_TABS_INDEX) => {
            const { estado, ...rest } = params;
            const tabEstados = TAB_ESTADOS[tabIndex];
            // "estado" puntual (elegido a mano o llegado por navegación, ej. desde
            // una card del Home) tiene prioridad sobre el bucket propio de la tab.
            const apiParams = {
                ...rest,
                ...(estado !== undefined
                    ? { idsEstado: [estado] }
                    : tabEstados !== undefined
                      ? { idsEstado: tabEstados }
                      : {})
            };
            getVisitasRoam(removeEmptyFields(apiParams));
        },
        [getVisitasRoam]
    );

    useEffect(() => {
        fetchVisitas(initialFiltros, initialTabIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFiltroChange = useCallback(
        (campo: keyof VisitasRoamFiltros, valor: string | number | boolean | undefined) => {
            let nuevosFiltros = { ...filtros, [campo]: valor, skip: 0 };

            if (campo === 'idProvinciaAccidente') {
                nuevosFiltros = { ...nuevosFiltros, idLocalidadAccidente: undefined };
            }

            if (campo === 'idOrganismoCentral') {
                nuevosFiltros = {
                    ...nuevosFiltros,
                    idOrganismo: undefined,
                    idEstablecimiento: undefined
                };
            }

            if (campo === 'idOrganismo') {
                nuevosFiltros = { ...nuevosFiltros, idEstablecimiento: undefined };
            }

            setFiltros(nuevosFiltros);

            const esTextoLibre = ['empleado', 'nroRoam', 'lugarOcurrencia'].includes(
                campo
            );

            if (esTextoLibre) {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => {
                    fetchVisitas(nuevosFiltros, currentTabIndex);
                }, DEBOUNCE_MS);
            } else {
                fetchVisitas(nuevosFiltros, currentTabIndex);
            }
        },
        [filtros, fetchVisitas, currentTabIndex]
    );

    const onLimpiarFiltros = useCallback(() => {
        setFiltros(FILTROS_INICIALES);
        fetchVisitas(FILTROS_INICIALES, currentTabIndex);
    }, [fetchVisitas, currentTabIndex]);

    const handleTabClick = useCallback(
        (_: React.SyntheticEvent, newTab: number) => {
            const tab = newTab as VISIT_TABS_INDEX;
            setCurrentTabIndex(tab);
            setFiltros(FILTROS_INICIALES);
            fetchVisitas(FILTROS_INICIALES, tab);
        },
        [fetchVisitas]
    );

    const onPaginationChange = useCallback(
        (newFilters: { skip?: number; take?: number }) => {
            const actualizados = { ...filtros, ...newFilters };
            setFiltros(actualizados);
            fetchVisitas(actualizados, currentTabIndex);
        },
        [filtros, fetchVisitas, currentTabIndex]
    );

    const visitas = result.data?.data?.data ?? [];
    const totalCount = result.data?.data?.count ?? 0;
    const loading = result.isFetching ?? false;
    const error = result.isError ? 'Error al cargar las visitas ROAM.' : null;

    return {
        visitas,
        totalCount,
        loading,
        error,
        filtros,
        currentTabIndex,
        handleTabClick,
        onFiltroChange,
        onLimpiarFiltros,
        onPaginationChange
    };
}

export default useVisitasRoam;
